import { BaseService } from "./baseService";
import { Attachment } from "../types";
import { fileToBase64, downloadBlob } from "../utils/apiHelpers";
import apiClient, { fileClient } from "./api";

class AttachmentService extends BaseService<Attachment> {
  constructor() {
    super("Attachment");
    this.setCacheStrategy("no-cache");
  }

  async uploadAttachment(
    taskId: string,
    file: File,
    parentType: string = "Task",
  ): Promise<Attachment> {
    const base64File = await fileToBase64(file);

    const response = await apiClient.post<Attachment>(
      "/Attachment",
      {
        file: base64File,
        name: file.name,
        type: file.type,
        role: "Attachment",
        parentType: parentType,
        field: parentType === "Project" ? "cAttachment" : "attachments",
        size: file.size,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  }

  async updateAttachment(
    attachmentId: string,
    data: Partial<Attachment>,
  ): Promise<Attachment> {
    return this.update(attachmentId, data);
  }

  async deleteAttachment(
    taskId: string,
    attachmentId: string,
    parentType: string = "Task",
  ): Promise<void> {
    await this.delete(attachmentId);
    let task: any;
    if (parentType === "ProjectTask") {
      const { getProjectTaskById } = await import("./projectService");
      task = await getProjectTaskById(taskId);
    } else {
      const { getTaskById } = await import("./taskService");
      task = await getTaskById(taskId);
    }

    if (!task.attachmentsIds || task.attachmentsIds.length === 0) {
      return;
    }

    const remainingIds = task.attachmentsIds.filter(
      (id: string) => id !== attachmentId,
    );
    const remainingNames: Record<string, string> = {};
    const remainingTypes: Record<string, string> = {};

    remainingIds.forEach((id: string) => {
      if (task.attachmentsNames?.[id]) {
        remainingNames[id] = task.attachmentsNames[id];
      }
      if (task.attachmentsTypes?.[id]) {
        remainingTypes[id] = task.attachmentsTypes[id];
      }
    });

    const endpoint =
      parentType === "ProjectTask"
        ? `/ProjectTask/${taskId}`
        : `/Task/${taskId}`;
    await apiClient.put(endpoint, {
      attachmentsIds: remainingIds,
      attachmentsNames: remainingNames,
      attachmentsTypes: remainingTypes,
    });

    const {
      removeFromCache,
      generateCacheKey,
      getCacheKeys,
      invalidateListCache,
    } = await import("../storage/cacheManager");
    if (parentType === "ProjectTask") {
      const taskCacheKey = generateCacheKey(`/ProjectTask/${taskId}`, {});
      await removeFromCache(taskCacheKey);
      try {
        const { getProjectTaskById } = await import("./projectService");
        const task = await getProjectTaskById(taskId);
        if (task.projectId) {
          const allKeys = await getCacheKeys();
          const planCacheKeys = allKeys.filter(
            (key) =>
              key.includes(`/Project/${task.projectId}/plan`) ||
              key.includes(`Project/${task.projectId}/plan`),
          );
          await Promise.all(planCacheKeys.map((key) => removeFromCache(key)));
        }
      } catch (err) {}
    } else if (parentType === "Task") {
      const taskCacheKey = generateCacheKey(`/Task/${taskId}`, {});
      await removeFromCache(taskCacheKey);

      await invalidateListCache("tasks");
    }
  }

  async getTaskAttachments(
    taskId: string,
    parentType: string = "Task",
  ): Promise<Attachment[]> {
    let task: any;
    if (parentType === "ProjectTask") {
      const { getProjectTaskById } = await import("./projectService");
      task = await getProjectTaskById(taskId);
    } else {
      const { getTaskById } = await import("./taskService");
      task = await getTaskById(taskId);
    }

    if (!task.attachmentsIds || task.attachmentsIds.length === 0) {
      return [];
    }

    return task.attachmentsIds.map((id: string) => ({
      id,
      name: task.attachmentsNames?.[id] || "Unknown",
      url: `/Attachment/file/${id}`,
      type: task.attachmentsTypes?.[id] || "application/octet-stream",
      parentType: parentType === "ProjectTask" ? "ProjectTask" : "Task",
      createdById: task.createdById,
    }));
  }

  async downloadAttachment(
    attachmentId: string,
    fileName: string,
  ): Promise<void> {
    try {
      const response = await apiClient.get(`/Attachment/file/${attachmentId}`, {
        responseType: "blob",
      });
      downloadBlob(response.data, fileName);
    } catch (error) {
      console.error("Ошибка при скачивании файла:", error);
      throw error;
    }
  }

  async getAttachmentBlob(attachmentId: string): Promise<Blob> {
    const response = await apiClient.get(`/Attachment/file/${attachmentId}`, {
      responseType: "blob",
    });
    return response.data;
  }
}

export const attachmentService = new AttachmentService();

export type { Attachment };
export const uploadAttachment = (
  taskId: string,
  file: File,
  parentType?: string,
) => attachmentService.uploadAttachment(taskId, file, parentType);
export const updateAttachment = (
  attachmentId: string,
  data: Partial<Attachment>,
) => attachmentService.updateAttachment(attachmentId, data);
export const deleteAttachment = (
  taskId: string,
  attachmentId: string,
  parentType?: string,
) => attachmentService.deleteAttachment(taskId, attachmentId, parentType);
export const getTaskAttachments = (taskId: string, parentType?: string) =>
  attachmentService.getTaskAttachments(taskId, parentType);
export const downloadAttachment = (attachmentId: string, fileName: string) =>
  attachmentService.downloadAttachment(attachmentId, fileName);
export const getAttachmentBlob = (attachmentId: string) =>
  attachmentService.getAttachmentBlob(attachmentId);
